import React, { useState } from "react";
import { motion } from "framer-motion";

const headlineContainerVariants = {
  hidden: {},
  visible: (delayChildren = 0) => ({
    transition: {
      staggerChildren: 0.08,
      delayChildren: delayChildren,
    },
  }),
};

const letterVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1.1,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const avatarContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.18,
      delayChildren: 0.9,
    },
  },
};

const avatarItemVariants = {
  hidden: { opacity: 0, scale: 0.75 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  },
};

export default function SkinCareHeaderNatural({ className }) {
  const [ordered, setOrdered] = useState(false);

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Big+Shoulders+Display:wght@400;600;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <div className={"w-full min-h-screen bg-[#FDFDFD] text-[#000] antialiased selection:bg-[#B9F33F] selection:text-black py-6 px-4 md:px-8 lg:px-12 flex flex-col items-center " + (className || "")}>
        <div className="w-full max-w-[1520px] flex flex-col gap-[10px]">

          <header className="w-full flex justify-between items-center pt-2">
            <div className="flex items-center gap-3">
              <motion.div
                className="flex items-center -space-x-3 overflow-hidden p-0.5"
                variants={avatarContainerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <motion.img
                  variants={avatarItemVariants}
                  src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=120&q=80"
                  alt="User Reviewer 1"
                  referrerPolicy="no-referrer"
                  style={{
                    width: "36px",
                    height: "36px",
                    aspectRatio: "1/1",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #FFFFFF",
                  }}
                />
                <motion.img
                  variants={avatarItemVariants}
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=120&q=80"
                  alt="User Reviewer 2"
                  referrerPolicy="no-referrer"
                  style={{
                    width: "36px",
                    height: "36px",
                    aspectRatio: "1/1",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #FFFFFF",
                  }}
                />
                <motion.img
                  variants={avatarItemVariants}
                  src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=120&q=80"
                  alt="User Reviewer 3"
                  referrerPolicy="no-referrer"
                  style={{
                    width: "36px",
                    height: "36px",
                    aspectRatio: "1/1",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #FFFFFF",
                  }}
                />
              </motion.div>

              <motion.span
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.95, ease: [0.16, 1, 0.3, 1] }}
                style={{
                  color: "#000000",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "normal",
                }}
              >
                23K User Glow with Confidence
              </motion.span>
            </div>

            <motion.button
              aria-label="Toggle Menu"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="cursor-pointer flex items-center justify-center shrink-0"
              style={{
                width: "36px",
                height: "36px",
                aspectRatio: "1/1",
                borderRadius: "8px",
                backgroundColor: "#B9F33F",
                border: "none",
                padding: 0,
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#000000"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </svg>
            </motion.button>
          </header>

          <div className="w-full flex flex-col gap-0">

            <div className="w-full flex flex-col lg:flex-row justify-between items-start gap-2 lg:gap-4">
              <motion.h1
                className="tracking-tighter"
                style={{
                  color: "#000000",
                  fontFamily: '"Big Shoulders Display", sans-serif',
                  fontSize: "clamp(80px, 22vw, 344.248px)",
                  fontStyle: "normal",
                  fontWeight: 700,
                  lineHeight: "0.9",
                  letterSpacing: "-6.885px",
                  margin: 0,
                }}
                variants={headlineContainerVariants}
                initial="hidden"
                whileInView="visible"
                custom={0}
                viewport={{ once: true }}
              >
                {"Natural".split("").map((char, index) => (
                  <motion.span
                    key={"natural-" + index}
                    variants={letterVariants}
                    className="inline-block"
                  >
                    {char}
                  </motion.span>
                ))}
              </motion.h1>

              <div className="w-full lg:w-auto flex justify-end">
                <motion.img
                  src="https://cdn.jiro.build/Natural%20Glow/Images/Header%20sm%20image.png"
                  alt="Cosmetic bloom texture"
                  referrerPolicy="no-referrer"
                  className="w-full max-w-[453px] object-cover object-top shadow-sm"
                  style={{
                    width: "453px",
                    height: "350px",
                    borderRadius: "12px",
                    maxWidth: "100%",
                  }}
                  initial={{ opacity: 0, scale: 1.12 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 1.6,
                    delay: 0.95,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                />
              </div>
            </div>

            <div className="w-full flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 lg:gap-4">

              <motion.div
                className="text-left"
                style={{
                  color: "#D2D2D2",
                  fontFamily: '"Big Shoulders Display", sans-serif',
                  fontSize: "clamp(70px, 17vw, 260px)",
                  fontStyle: "normal",
                  fontWeight: 700,
                  lineHeight: "0.9",
                  letterSpacing: "-5.2px",
                  textAlign: "left",
                }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 1.1,
                  delay: 0.95,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                <motion.span
                  className="inline-block"
                  animate={{
                    color: ["#D2D2D2", "#F0F0F0", "#D2D2D2"],
                    textShadow: [
                      "0 0 0px rgba(255,255,255,0)",
                      "0 0 20px rgba(255,255,255,0.6)",
                      "0 0 0px rgba(255,255,255,0)",
                    ],
                  }}
                  transition={{
                    duration: 3.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1.8,
                  }}
                >
                  Glow
                </motion.span>
              </motion.div>

              <motion.p
                className="self-center my-2 lg:my-0 text-left"
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
                style={{
                  width: "349px",
                  maxWidth: "100%",
                  color: "#353535",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "18px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "24px",
                  textAlign: "left",
                }}
              >
                We're committed to helping you achieve radiant, healthy skin. Our expert team offers personalized treatments.
              </motion.p>

              <motion.div
                className="text-left"
                style={{
                  color: "#000000",
                  fontFamily: '"Big Shoulders Display", sans-serif',
                  fontSize: "clamp(80px, 21vw, 334px)",
                  fontStyle: "normal",
                  fontWeight: 700,
                  lineHeight: "0.9",
                  letterSpacing: "-6.68px",
                  textAlign: "left",
                }}
                variants={headlineContainerVariants}
                initial="hidden"
                whileInView="visible"
                custom={0.48}
                viewport={{ once: true }}
              >
                {"Shines".split("").map((char, index) => (
                  <motion.span
                    key={"shines-" + index}
                    variants={letterVariants}
                    className="inline-block"
                  >
                    {char}
                  </motion.span>
                ))}
              </motion.div>
            </div>

            <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-start gap-6 pb-6">
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 1.15 }}
                style={{
                  color: "#626262",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "20px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "24px",
                  whiteSpace: "pre-line",
                  textAlign: "left",
                }}
              >
                {"Expert Skincare Solutions\nFor Healthier, Radiant Skin"}
              </motion.div>

              <motion.button
                onClick={() => setOrdered(!ordered)}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.8,
                  delay: 1.2,
                  type: "spring",
                  stiffness: 180,
                  damping: 22,
                }}
                whileHover="hover"
                whileTap={{ scale: 0.95 }}
                className="shadow-sm cursor-pointer"
                style={{
                  display: "inline-flex",
                  padding: "12px 12px 12px 18px",
                  alignItems: "center",
                  gap: "9px",
                  borderRadius: "12px",
                  background: "#B9F33F",
                  border: "none",
                }}
              >
                <motion.span
                  variants={{
                    hover: { scale: 1.03 },
                  }}
                  transition={{ duration: 0.2 }}
                  style={{
                    color: "#242424",
                    textAlign: "center",
                    fontFamily: '"Big Shoulders Display", sans-serif',
                    fontSize: "18px",
                    fontStyle: "normal",
                    fontWeight: 600,
                    lineHeight: "18px",
                    textTransform: "uppercase",
                    display: "inline-block",
                  }}
                >
                  {ordered ? "ADDED TO CART" : "ORDER YOUR NOW"}
                </motion.span>

                <motion.svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#242424"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  variants={{
                    hover: { x: 3, y: -3 },
                  }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <path d="M7 17L17 7" />
                  <path d="M7 7h10v10" />
                </motion.svg>
              </motion.button>
            </div>
          </div>

          <div className="w-full flex justify-center pt-2">
            <motion.div
              className="relative w-full overflow-hidden shadow-sm"
              style={{
                width: "100%",
                maxWidth: "1480px",
                height: "auto",
                minHeight: "380px",
                maxHeight: "730px",
                aspectRatio: "1480/730",
                borderRadius: "24px",
              }}
              initial={{ opacity: 0, scale: 1.04 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.8,
                delay: 0.2,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                poster="https://cdn.jiro.build/Natural%20Glow/Images/Header%20sm%20image.png"
                className="w-full h-full object-cover rounded-[24px]"
              >
                <source
                  src="https://cdn.jiro.build/Natural%20Glow/Video/Water_flowing_around_sleeping_mask_202607301026.mp4"
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>

              <motion.div
                className="absolute top-6 left-6 md:top-10 md:left-10 bg-white/95 backdrop-blur-md rounded-[20px] p-5 shadow-lg hidden sm:flex flex-col justify-between border border-black/5 z-10"
                style={{
                  width: "280px",
                  height: "210px",
                  borderRadius: "20px",
                  backgroundColor: "#FFFFFF",
                }}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.4 }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold tracking-widest uppercase bg-[#E2FF99] text-[#242121] px-2.5 py-1 rounded-full">
                    SPECIAL CARE
                  </span>
                  <span className="text-xs font-semibold text-amber-500 flex items-center gap-1">
                    ★ 4.9
                  </span>
                </div>

                <div>
                  <h3
                    className="text-xl font-extrabold uppercase text-[#242121] leading-tight"
                    style={{ fontFamily: '"Big Shoulders Display", sans-serif' }}
                  >
                    BIOMOS SLEEPING MASK
                  </h3>
                  <p className="text-[14px] text-[#242121]/80 mt-1 leading-snug">
                    Plant-based formula infused with organic alpine moss & squalane for intense overnight hydration.
                  </p>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-black/10 text-xs">
                  <span className="font-bold text-[#4D5B44] text-[12px]">100% Organic</span>
                  <button
                    onClick={() => setOrdered(true)}
                    className="text-xs font-bold text-[#242121] hover:text-[#4D5B44] transition-colors flex items-center gap-1 group cursor-pointer"
                  >
                    <span>{ordered ? "Added ✓" : "Shop Now"}</span>
                    <span className="group-hover:translate-x-0.5 transition-transform">→</span>
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </div>

        </div>
      </div>
    </>
  );
}